import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { api } from "../api/endpoints";
import { wsUrl } from "../api/client";
import type { AlertEvent, RealtimeMessage } from "../api/types";
import { backoffDelay } from "../lib/backoff";
import { parseTs } from "../lib/time";
import { alarm } from "./alarm";

// One app-wide WebSocket to /ws. Pages subscribe to messages; the provider
// itself owns the "active alarm" list that drives the red banner.

export type ConnState = "connecting" | "open" | "reconnecting";

export interface ConnInfo {
  state: ConnState;
  /** When the next reconnect attempt fires (ms epoch), while reconnecting. */
  retryAt: number | null;
  /** Increments on every successful (re)connect so pages can resync. */
  epoch: number;
}

type Handler = (msg: RealtimeMessage) => void;

interface RealtimeApi {
  conn: ConnInfo;
  activeAlerts: AlertEvent[];
  subscribe: (fn: Handler) => () => void;
  ack: (id: string) => Promise<void>;
  silence: () => void;
  reconnectNow: () => void;
  /** Wrap a self-raised alert so this browser does not sound its own alarm. */
  raiseOwn: (fn: () => Promise<AlertEvent>) => Promise<AlertEvent>;
}

const Ctx = createContext<RealtimeApi | null>(null);

const PING_MS = 25_000;
const DEAD_AFTER_MS = 70_000;
/** On (re)connect, unacked panic alerts newer than this also show the banner. */
const RESYNC_WINDOW_MS = 2 * 60 * 60 * 1000;

export function isPanic(e: AlertEvent): boolean {
  return e.kind === "button" || e.kind === "manual";
}

export function RealtimeProvider({ token, children }: { token: string; children: ReactNode }) {
  const [conn, setConn] = useState<ConnInfo>({ state: "connecting", retryAt: null, epoch: 0 });
  const [activeAlerts, setActiveAlerts] = useState<AlertEvent[]>([]);
  const handlers = useRef(new Set<Handler>());
  const knownIds = useRef(new Set<string>());
  const ownIds = useRef(new Set<string>());
  const ownPending = useRef(0);
  const kick = useRef<() => void>(() => undefined);

  const addAlerts = useCallback((events: AlertEvent[], sound: boolean) => {
    const fresh = events.filter((e) => isPanic(e) && !e.acked_by && !knownIds.current.has(e.id));
    if (fresh.length === 0) return;
    fresh.forEach((e) => knownIds.current.add(e.id));
    setActiveAlerts((prev) => {
      const ids = new Set(prev.map((p) => p.id));
      return [...fresh.filter((e) => !ids.has(e.id)), ...prev].sort(
        (a, b) => (parseTs(b.created_at) ?? 0) - (parseTs(a.created_at) ?? 0),
      );
    });
    const someoneElse = fresh.some((e) => !(e.kind === "manual" && (ownPending.current > 0 || ownIds.current.has(e.id))));
    if (sound && someoneElse) alarm.start();
  }, []);

  const removeAlert = useCallback((id: string) => {
    setActiveAlerts((prev) => prev.filter((e) => e.id !== id));
  }, []);

  // The alarm stops as soon as nothing is left to acknowledge.
  useEffect(() => {
    if (activeAlerts.length === 0) alarm.stop();
  }, [activeAlerts]);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let lastMsgAt = Date.now();
    let disposed = false;
    let everOpened = false;

    const clearTimers = () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (pingTimer) clearInterval(pingTimer);
      retryTimer = null;
      pingTimer = null;
    };

    const resync = (sound: boolean) => {
      api
        .events({ limit: 20 })
        .then((events) => {
          const cutoff = Date.now() - RESYNC_WINDOW_MS;
          addAlerts(
            events.filter((e) => (parseTs(e.created_at) ?? 0) >= cutoff),
            sound,
          );
          // Drop banner entries that were acked while we were away.
          const acked = new Set(events.filter((e) => e.acked_by).map((e) => e.id));
          setActiveAlerts((prev) => (prev.some((e) => acked.has(e.id)) ? prev.filter((e) => !acked.has(e.id)) : prev));
        })
        .catch(() => undefined);
    };

    const schedule = () => {
      if (disposed) return;
      const delay = backoffDelay(attempt++);
      setConn((c) => ({ ...c, state: "reconnecting", retryAt: Date.now() + delay }));
      retryTimer = setTimeout(connect, delay);
    };

    const connect = () => {
      if (disposed) return;
      clearTimers();
      setConn((c) => ({ ...c, state: c.epoch === 0 ? "connecting" : "reconnecting", retryAt: null }));
      let opened = false;
      try {
        ws = new WebSocket(wsUrl("/ws", { token }));
      } catch {
        schedule();
        return;
      }
      const sock = ws;

      sock.onopen = () => {
        opened = true;
        const first = !everOpened;
        everOpened = true;
        attempt = 0;
        lastMsgAt = Date.now();
        setConn((c) => ({ state: "open", retryAt: null, epoch: c.epoch + 1 }));
        resync(!first);
        pingTimer = setInterval(() => {
          if (Date.now() - lastMsgAt > DEAD_AFTER_MS) {
            sock.close(4000, "no traffic");
            return;
          }
          if (sock.readyState === WebSocket.OPEN) sock.send(JSON.stringify({ type: "ping" }));
        }, PING_MS);
      };

      sock.onmessage = (ev: MessageEvent) => {
        lastMsgAt = Date.now();
        if (typeof ev.data !== "string") return;
        let msg: RealtimeMessage;
        try {
          msg = JSON.parse(ev.data) as RealtimeMessage;
        } catch {
          return;
        }
        if (msg.type === "ping") {
          sock.send(JSON.stringify({ type: "pong" }));
          return;
        }
        if (msg.type === "event") addAlerts([msg.event], true);
        if (msg.type === "event_ack") removeAlert(msg.event.id);
        handlers.current.forEach((h) => {
          try {
            h(msg);
          } catch (err) {
            console.error("realtime handler failed", err);
          }
        });
      };

      sock.onclose = () => {
        if (ws === sock) ws = null;
        clearTimers();
        if (disposed) return;
        // Never opened: maybe the token is dead. A REST call will trip the
        // global 401 handler and sign out if so.
        if (!opened) api.me().catch(() => undefined);
        schedule();
      };
      sock.onerror = () => {
        // onclose follows and handles the retry.
      };
    };

    kick.current = () => {
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
      attempt = 0;
      connect();
    };

    const onOnline = () => kick.current();
    const onVisible = () => {
      if (document.visibilityState === "visible") kick.current();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    connect();

    return () => {
      disposed = true;
      clearTimers();
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
      if (ws) {
        ws.onclose = null;
        ws.close(1000, "bye");
      }
      alarm.stop();
    };
  }, [token, addAlerts, removeAlert]);

  const subscribe = useCallback((fn: Handler) => {
    handlers.current.add(fn);
    return () => {
      handlers.current.delete(fn);
    };
  }, []);

  const ack = useCallback(
    async (id: string) => {
      const ev = await api.ackEvent(id);
      removeAlert(id);
      handlers.current.forEach((h) => h({ type: "event_ack", event: ev }));
    },
    [removeAlert],
  );

  const silence = useCallback(() => alarm.stop(), []);
  const reconnectNow = useCallback(() => kick.current(), []);

  const raiseOwn = useCallback(async (fn: () => Promise<AlertEvent>) => {
    ownPending.current += 1;
    try {
      const ev = await fn();
      ownIds.current.add(ev.id);
      return ev;
    } finally {
      // Keep the guard up briefly in case the socket copy is still in flight.
      setTimeout(() => {
        ownPending.current = Math.max(0, ownPending.current - 1);
      }, 3000);
    }
  }, []);

  const value = useMemo<RealtimeApi>(
    () => ({ conn, activeAlerts, subscribe, ack, silence, reconnectNow, raiseOwn }),
    [conn, activeAlerts, subscribe, ack, silence, reconnectNow, raiseOwn],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRealtime(): RealtimeApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useRealtime outside RealtimeProvider");
  return v;
}

/** Subscribe to realtime messages for the lifetime of a component. */
export function useRealtimeMessages(handler: Handler): void {
  const { subscribe } = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => subscribe((m) => ref.current(m)), [subscribe]);
}

export function useAlarmState(): { playing: boolean; blocked: boolean } {
  const playing = useSyncExternalStore(
    (cb) => alarm.subscribe(cb),
    () => alarm.playing,
  );
  const blocked = useSyncExternalStore(
    (cb) => alarm.subscribe(cb),
    () => alarm.blocked,
  );
  return { playing, blocked };
}
